import { useMemo, useState } from "react";
import { useCoffees } from "../hooks/useCoffees";
import { CoffeeCard } from "../components/CoffeeCard";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { KIND_OPTIONS, isTea, kindLabel } from "../types";

export function Browse() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("coffee");
  const [country, setCountry] = useState("");
  const [proc, setProc] = useState("");
  const [grade, setGrade] = useState("");
  const [roaster, setRoaster] = useState("");
  const [sort, setSort] = useState("created");
  const [view, setView] = useState<"grid" | "list">("grid");

  const params = useMemo(() => {
    const p: Record<string, string> = { sort };
    if (q) p.q = q;
    if (kind) p.kind = kind;
    if (country) p.country = country;
    if (proc) p.process = proc;
    if (grade) p.grade = grade;
    if (roaster) p.roaster = roaster;
    return p;
  }, [q, kind, country, proc, grade, roaster, sort]);

  const { data, refresh } = useCoffees(params);

  const facets = useMemo(() => {
    if (!data) return { countries: [], procs: [], grades: [], roasters: [] };
    const countries = new Set<string>();
    const procs = new Set<string>();
    const grades = new Set<string>();
    const roasters = new Set<string>();
    for (const c of data) {
      countries.add(c.country);
      procs.add(c.process);
      roasters.add(c.roaster);
      if (c.grade) c.grade.split(",").map((s) => s.trim()).filter(Boolean).forEach((g) => grades.add(g));
    }
    return {
      countries: [...countries].sort(),
      procs: [...procs].sort(),
      grades: [...grades].sort(),
      roasters: [...roasters].sort(),
    };
  }, [data]);

  const onUse = async (id: number) => { await api.use(id); refresh(); };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <div className="eyebrow">The Vault · Browse</div>
        <h1 className="font-display text-3xl">{data ? `${data.length} item${data.length === 1 ? "" : "s"}` : "Loading…"}</h1>
      </header>

      <div className="flex flex-wrap gap-2">
        <KindChip label="All" active={kind === ""} onClick={() => setKind("")} />
        {KIND_OPTIONS.map((k) => (
          <KindChip key={k} label={kindLabel(k)} active={kind === k} onClick={() => setKind(k)} />
        ))}
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-end pb-4 border-b border-paper-200">
        <div>
          <label className="label">Search</label>
          <input className="input" placeholder="Variety, country, roaster, farmer, flavor…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex items-end gap-2 text-[11px] uppercase tracking-widest">
          <button onClick={() => setView("grid")} className={`pb-0.5 border-b ${view === "grid" ? "border-ink text-ink" : "border-transparent text-paper-400"}`}>Grid</button>
          <span className="text-paper-300">/</span>
          <button onClick={() => setView("list")} className={`pb-0.5 border-b ${view === "list" ? "border-ink text-ink" : "border-transparent text-paper-400"}`}>List</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
        <Filter label="Country" value={country} onChange={setCountry} options={facets.countries} />
        <Filter label="Process" value={proc} onChange={setProc} options={facets.procs} />
        <Filter label="Grade" value={grade} onChange={setGrade} options={facets.grades} />
        <Filter label="Roaster" value={roaster} onChange={setRoaster} options={facets.roasters} />
        <Filter label="Sort" value={sort} onChange={setSort} options={[
          { v: "created", l: "Recently added" },
          { v: "roast_date", l: "Roast date" },
          { v: "freeze_date", l: "Freeze date" },
          { v: "portions", l: "Portions left" },
          { v: "name", l: "A → Z" },
        ]} />
      </div>

      {!data ? null : data.length === 0 ? (
        <div className="py-16 text-center text-paper-500">
          Nothing matches. <Link to="/add" className="text-ink underline">Add one →</Link>
        </div>
      ) : view === "grid" ? (
        <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((c) => <CoffeeCard key={c.id} coffee={c} onUse={onUse} />)}
        </div>
      ) : (
        <div className="border-t border-paper-200">
          {data.map((c) => (
            <Link key={c.id} to={`/coffee/${c.id}`} className="grid grid-cols-[1fr_auto] gap-4 items-baseline py-3 border-b border-paper-200 hover:bg-paper-100 px-1">
              <div>
                <div className="font-medium text-ink">{c.variety} <span className="text-paper-400 font-normal">/ {c.process}</span></div>
                <div className="text-xs text-paper-500">{c.country}{c.region ? ` · ${c.region}` : ""} · {c.roaster}</div>
              </div>
              <div className={`tabular-nums text-xs ${!isTea(c.kind) && c.portions <= 2 ? "text-red-700" : "text-paper-500"}`}>
                {isTea(c.kind) ? `${c.portions} bottle${c.portions === 1 ? "" : "s"}` : `${c.portions} × ${c.grams}g`}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function KindChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs uppercase tracking-widest border ${active ? "bg-ink text-paper-50 border-ink" : "bg-paper-50 text-paper-600 border-paper-300 hover:border-ink hover:text-ink"}`}
    >
      {label}
    </button>
  );
}

type Opt = string | { v: string; l: string };
function Filter({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: Opt[];
}) {
  return (
    <div className="flex flex-col">
      <span className="label">{label}</span>
      <select className="bg-transparent border-0 border-b border-paper-300 text-sm py-1 pr-6 focus:outline-none focus:border-ink" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.v;
          const l = typeof o === "string" ? o : o.l;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </div>
  );
}
